import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AssetsRelationTypeSchemaNullable } from '@iconik-io/iconik-api/src/services/assets';

import type {
  AssetRelationsAppState,
  AssetsRelationCreated,
  AssetsRelationLabel,
} from './types';

export const initialState: AssetRelationsAppState = {
  relations: {},
  deletedRelations: {},
  relationsTypes: [],
  relationsLabels: {},
};

type RelationDeleted = {
  source: string;
  target: string;
  relation: string;
};

export const assetRelationsSlice = createSlice({
  name: 'asset_relations',
  initialState,
  reducers: {
    setAssetRelations: (
      state,
      action: PayloadAction<{ assetId: string; relationIds: string[] }>,
    ) => {
      state.relations[action.payload.assetId] = action.payload.relationIds;
    },

    clearAssetRelations: (state, action: PayloadAction<string>) => {
      state.relations[action.payload] = [];
      state.deletedRelations[action.payload] = [];
    },

    setAssetRelationsTypes: (
      state,
      action: PayloadAction<AssetsRelationTypeSchemaNullable[]>,
    ) => {
      state.relationsLabels = action.payload.reduce<
        Record<string, AssetsRelationLabel>
      >((dictionary, next) => {
        dictionary[next.name] = {
          destination_label: next.destination_label,
          source_label: next.source_label,
        };
        return dictionary;
      }, {});

      state.relationsTypes = action.payload.map((relation) => ({
        value: relation.name,
        label:
          relation.destination_label === relation.source_label
            ? relation.destination_label
            : `${relation.source_label}/${relation.destination_label}`,
        description: relation.description ?? undefined,
        is_directional: relation.is_directional,
      }));
    },

    assetRelationCreated: (
      state,
      action: PayloadAction<AssetsRelationCreated>,
    ) => {
      const { source, target, type } = action.payload;
      if (!source) {
        return;
      }
      const sourceDeletedRelations = state.deletedRelations[source];
      if (!sourceDeletedRelations) {
        return;
      }
      state.deletedRelations[source] = sourceDeletedRelations.filter(
        (item) => !(item.id === target && item.relation === type),
      );
    },

    assetRelationDeleted: (state, action: PayloadAction<RelationDeleted>) => {
      const { source, target, relation } = action.payload;
      const deletedRelations = state.deletedRelations[source] ?? [];
      deletedRelations.push({ id: target, relation });
      state.deletedRelations[source] = deletedRelations;
    },
  },
});

export const {
  assetRelationCreated,
  assetRelationDeleted,
  clearAssetRelations,
  setAssetRelations,
  setAssetRelationsTypes,
} = assetRelationsSlice.actions;

export const assetRelationsReducer = assetRelationsSlice.reducer;
